import React from "react";
import ReactDOM from "react-dom/client";
import { AdMeshIntentAssistant } from "./components/AdMeshIntentAssistant";
import { AdMeshProvider, type PageContextMode } from "./context/AdMeshProvider";
import type { PlatformResponse } from "./types";

if (typeof globalThis !== "undefined" && typeof (globalThis as { process?: unknown }).process === "undefined") {
  (globalThis as { process?: { env: Record<string, string> } }).process = {
    env: {
      NODE_ENV: "production",
      ADMESH_ENV: "production",
    },
  };
}

type WelcomeAlignment = "left" | "center";
type DeploymentMode = "floating" | "inline";
type FloatingPosition = "bottom-right" | "bottom-left" | "top-right" | "top-left";
type DeviceTargeting = "all" | "mobile_only" | "desktop_only";
type AssistantSize = "sm" | "md" | "lg" | "xl";
type AssistantSurface = "page" | "ad-slot";
type AdSlotBehavior = "sticky" | "expand";
type SuggestionStyle = "carousel" | "stacked";

/**
 * Standard IAB banner slot types. Setting `data-slot-type` automatically
 * infers `data-surface="ad-slot"` and the correct width/height.
 */
export type AdSlotType =
  | "mobile-leaderboard" // 320×100
  | "medium-rectangle"   // 300×250
  | "leaderboard"        // 728×90
  | "half-page"          // 300×600
  | "large-rectangle"    // 336×280
  | "skyscraper"         // 120×600
  | "billboard";         // 970×250

const AD_SLOT_TYPE_DIMENSIONS: Record<AdSlotType, { width: number; height: number }> = {
  "mobile-leaderboard":  { width: 320, height: 100 },
  "medium-rectangle":    { width: 300, height: 250 },
  "leaderboard":         { width: 728, height: 90  },
  "half-page":           { width: 300, height: 600 },
  "large-rectangle":     { width: 336, height: 280 },
  "skyscraper":          { width: 120, height: 600 },
  "billboard":           { width: 970, height: 250 },
};

/** Default suggestion layout per IAB slot type (overridable via data-suggestion-style). */
const AD_SLOT_TYPE_SUGGESTION_STYLE: Record<AdSlotType, SuggestionStyle> = {
  "mobile-leaderboard":  "carousel",
  "medium-rectangle":    "carousel",
  "leaderboard":         "carousel",
  "large-rectangle":     "carousel",
  "billboard":           "carousel",
  "half-page":           "stacked",
  "skyscraper":          "stacked",
};

/** Hide compact-slot header for short formats with limited vertical space. */
const AD_SLOT_TYPE_SHOW_HEADER: Partial<Record<AdSlotType, boolean>> = {
  "mobile-leaderboard": false,
  "medium-rectangle": false,
  "leaderboard": false,
};

/** Hide compact-slot welcome title for very short formats. */
const AD_SLOT_TYPE_SHOW_WELCOME_MESSAGE: Partial<Record<AdSlotType, boolean>> = {
  "mobile-leaderboard": false,
  "leaderboard": false,
};

/** Hide compact-slot input composer for very short formats. */
const AD_SLOT_TYPE_SHOW_INPUT: Partial<Record<AdSlotType, boolean>> = {
  "mobile-leaderboard": false,
  "leaderboard": false,
};

/** Hide compact-slot AI disclaimer for very short formats. */
const AD_SLOT_TYPE_SHOW_DISCLAIMER: Partial<Record<AdSlotType, boolean>> = {
  "mobile-leaderboard": false,
  "leaderboard": false,
};

/** Slot types that only support expand (sticky is not allowed). */
const AD_SLOT_TYPE_BEHAVIOR: Partial<Record<AdSlotType, AdSlotBehavior>> = {
  "mobile-leaderboard": "expand",
  "leaderboard": "expand",
};

/** Mobile viewport fallbacks for desktop-oriented slot types. */
const AD_SLOT_TYPE_MOBILE_FALLBACK: Partial<Record<AdSlotType, AdSlotType>> = {
  "half-page": "medium-rectangle",
  "large-rectangle": "medium-rectangle",
  "skyscraper": "medium-rectangle",
  "billboard": "medium-rectangle",
  "leaderboard": "mobile-leaderboard",
};

interface PublicAssistantConfig {
  enabled: boolean;
  answer_scope: "publisher_only" | "publisher_plus_public";
  model_name: string;
  ads_enabled: boolean;
  assistant_name: string;
  show_assistant_name?: boolean;
  show_assistant_label?: boolean;
  welcome_title: string;
  welcome_description: string;
  welcome_alignment: WelcomeAlignment;
  input_placeholder: string;
  disclaimer_text: string;
  send_button_label: string;
  header_background_color: string;
  header_text_color: string;
  welcome_title_color: string;
  welcome_description_color: string;
  suggestion_background_color: string;
  suggestion_text_color: string;
  suggestion_style?: SuggestionStyle;
  input_background_color: string;
  input_text_color: string;
  send_button_background_color: string;
  send_button_text_color: "#000000" | "#ffffff";
  meta_text_color: string;
  logo_url?: string | null;
  deployment_mode: DeploymentMode;
  floating_position: FloatingPosition;
  inline_target_selector: string;
  device_targeting?: DeviceTargeting;
  page_rules_enabled: boolean;
  show_on_paths: string[];
  hide_on_paths: string[];
  allowed_countries?: string[];
}

interface PublicAssistantConfigResponse {
  config: PublicAssistantConfig;
  ready?: boolean;
  reason?: string | null;
  country?: string | null;
  runtime_api_key?: string | null;
  has_runtime_api_key?: boolean;
}

const COUNTRY_BLOCK_CACHE_TTL_MS = 60 * 1000;

interface CountryBlockCacheEntry {
  expiresAt: number;
  reason: string;
  country?: string | null;
}

interface AssistantSuggestion {
  id?: string;
  text: string;
  type?: "product" | "comparison" | "information" | "action";
  is_sponsored?: boolean;
  recommendation?: {
    recommendation_id: string;
    recommendation?: {
      format?: string;
      disclosure?: string;
      creative?: {
        brand_name?: string;
        headline?: string;
        description?: string;
        cta_text?: string;
        follow_up_suggestion?: string;
        logo_url?: string;
        image_urls?: string[];
        landing_page_url?: string;
        products?: Array<{
          product_id?: string;
          product_link?: string;
          product_name?: string;
          product_description?: string;
          product_price?: string | number;
          product_cta_label?: string;
        }>;
      };
    };
    tracking?: {
      exposure_url?: string;
      click_url?: string;
      followup_exposure_url?: string;
      followup_engagement_url?: string;
    };
  };
  metadata?: Record<string, unknown>;
}

interface ScriptOverrides {
  mode?: DeploymentMode;
  position?: FloatingPosition;
  size?: AssistantSize;
  surface?: AssistantSurface;
  slotType?: AdSlotType;
  width?: number;
  height?: number;
  suggestionStyle?: SuggestionStyle;
  showHeader?: boolean;
  showAssistantName?: boolean;
  showAssistantLabel?: boolean;
  showWelcomeMessage?: boolean;
  welcomeMessage?: string;
  targetSelector?: string;
  articleUrl?: string;
  contextMode?: PageContextMode;
  behavior?: AdSlotBehavior;
}

const EMBED_STYLE_ID = "admesh-publisher-assistant-embed-styles";
const DEFAULT_API_BASE_URL =
  (window as typeof window & { __ADMESH_API_BASE_URL__?: string }).__ADMESH_API_BASE_URL__ ||
  "https://api.useadmesh.com";

const EXPANDED_SIZE_CONFIG: Record<AssistantSize, { width: string; height: string; bodyHeight: string }> = {
  sm: { width: "320px", height: "400px", bodyHeight: "300px" },
  md: { width: "400px", height: "500px", bodyHeight: "400px" },
  lg: { width: "480px", height: "600px", bodyHeight: "500px" },
  xl: { width: "560px", height: "700px", bodyHeight: "600px" },
};

function ensureStyles() {
  if (document.getElementById(EMBED_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = EMBED_STYLE_ID;
  style.textContent = `
    .admesh-publisher-assistant-root {
      font-family: Inter, ui-sans-serif, system-ui, sans-serif;
      line-height: 1.4;
      color: #111827;
      --admesh-assistant-expanded-width: 400px;
      --admesh-assistant-expanded-height: 500px;
      --admesh-assistant-expanded-body-height: 400px;
    }
    .admesh-publisher-assistant-reactMount {
      box-sizing: border-box;
    }
    .admesh-publisher-assistant-reactMount[data-admesh-surface="ad-slot"] {
      width: 100%;
      height: 100%;
      max-width: 100%;
      max-height: 100%;
      box-sizing: border-box;
      position: relative;
      overflow: hidden;
    }
    .admesh-publisher-assistant-inline {
      height: 100%;
      min-height: 0;
    }
    .admesh-publisher-assistant-inlineSheetBackdrop {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.28);
      backdrop-filter: blur(6px);
      z-index: 2147483000;
      opacity: 1;
      transition: opacity 160ms ease;
    }
    .admesh-publisher-assistant-inlineSheetBackdrop.admesh-publisher-assistant-hidden {
      opacity: 0;
      pointer-events: none;
    }
    .admesh-publisher-assistant-sheetBackdrop {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.22);
      backdrop-filter: blur(6px);
      z-index: 2147483000;
      opacity: 1;
      transition: opacity 160ms ease;
    }
    .admesh-publisher-assistant-sheetBackdrop.admesh-publisher-assistant-hidden {
      opacity: 0;
      pointer-events: none;
    }
    .admesh-publisher-assistant-floating {
      position: fixed;
      z-index: 2147483000;
    }
    .admesh-publisher-assistant-floating.bottom-right { right: 20px; bottom: 20px; }
    .admesh-publisher-assistant-floating.bottom-left { left: 20px; bottom: 20px; }
    .admesh-publisher-assistant-floating.top-right { right: 20px; top: 20px; }
    .admesh-publisher-assistant-floating.top-left { left: 20px; top: 20px; }
    .admesh-publisher-assistant-panel {
      width: min(420px, calc(100vw - 32px));
      max-height: min(720px, calc(100vh - 90px));
      border: 1px solid rgba(17, 24, 39, 0.12);
      border-radius: 18px;
      background: #ffffff;
      box-shadow: 0 24px 60px rgba(15, 23, 42, 0.16);
      overflow: hidden;
      transition: width 160ms ease, max-height 160ms ease, transform 160ms ease;
    }
    .admesh-publisher-assistant-panel.expanded {
      position: fixed;
      top: auto;
      left: 50%;
      right: auto;
      bottom: 12px;
      transform: translateX(-50%);
      z-index: 2147483001;
      width: min(var(--admesh-assistant-expanded-width), calc(100vw - 24px));
      height: min(var(--admesh-assistant-expanded-height), calc(100vh - 24px));
      max-height: min(var(--admesh-assistant-expanded-height), calc(100vh - 24px));
      border-radius: 24px 24px 20px 20px;
      box-shadow: 0 28px 80px rgba(15, 23, 42, 0.26);
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-panel {
      display: flex;
      flex-direction: column;
      width: 100%;
      max-width: 100%;
      height: auto;
      max-height: none;
      min-height: 0;
      border-radius: 32px;
      padding: 14px;
      background: linear-gradient(180deg, #eef2ff 0%, #e9edff 100%);
      border: 0;
      box-shadow: none;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-header {
      display: none;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-headerActions {
      display: none;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-headerButton {
      background: rgba(63, 3, 127, 0.08);
      color: #3F037F;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-logo {
      background: rgba(63, 3, 127, 0.10);
      color: #3F037F;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-panel.expanded {
      position: fixed;
      top: auto;
      left: 50%;
      right: auto;
      bottom: 12px;
      transform: translateX(-50%);
      z-index: 2147483001;
      width: min(960px, calc(100vw - 24px));
      max-width: min(960px, calc(100vw - 24px));
      height: min(78vh, 760px);
      max-height: min(78vh, 760px);
      border-radius: 24px 24px 20px 20px;
      padding: 0;
      background: #ffffff;
      border: 1px solid rgba(17, 24, 39, 0.12);
      box-shadow: 0 28px 80px rgba(15, 23, 42, 0.26);
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-panel.expanded .admesh-publisher-assistant-header {
      display: flex !important;
      align-items: center;
      justify-content: space-between;
      padding: 14px 16px;
      background: #3F037F !important;
      color: #ffffff !important;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-panel.expanded .admesh-publisher-assistant-headerMain {
      display: flex;
      align-items: center;
      visibility: visible;
      opacity: 1;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-panel.expanded .admesh-publisher-assistant-headerActions {
      display: flex !important;
      visibility: visible;
      opacity: 1;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-panel.expanded .admesh-publisher-assistant-logo {
      display: flex;
      visibility: visible;
      opacity: 1;
      background: rgba(255, 255, 255, 0.15);
      color: #ffffff;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-panel.expanded .admesh-publisher-assistant-headerText {
      display: block;
      visibility: visible;
      opacity: 1;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-panel.expanded .admesh-publisher-assistant-headerText > div {
      color: #ffffff !important;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-panel.expanded .admesh-publisher-assistant-headerButton {
      background: rgba(255, 255, 255, 0.18);
      color: #ffffff !important;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-panel.expanded .admesh-publisher-assistant-welcome {
      display: block;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-panel.expanded .admesh-publisher-assistant-meta {
      display: block;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-panel.expanded .admesh-publisher-assistant-body {
      flex: 1;
      min-height: 0;
      overflow: hidden;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-body {
      flex: 0 0 auto;
      display: flex;
      flex-direction: column;
      gap: 10px;
      min-height: 0;
      overflow: hidden;
      padding: 16px;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-welcome {
      display: none;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-inputRow {
      border-radius: 999px;
      padding: 10px 12px 10px 16px;
      min-height: 64px;
      background: #ffffff;
      border: 0;
      box-shadow: 0 10px 28px rgba(63, 3, 127, 0.08), inset 0 0 0 1px rgba(17, 24, 39, 0.05);
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-input {
      font-size: 16px;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-send {
      border-radius: 999px;
      min-width: 44px;
      min-height: 44px;
      padding: 0 16px;
      box-shadow: 0 8px 20px rgba(63, 3, 127, 0.18);
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-suggestions {
      gap: 8px;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-suggestion {
      border-radius: 999px;
      border: 0;
      padding: 8px 14px;
      font-size: 13px;
      box-shadow: inset 0 0 0 1px rgba(17, 24, 39, 0.04);
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-meta {
      display: block;
      font-size: 10px;
      line-height: 1.35;
      color: #8a86a3;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-messages {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
    }
    .admesh-publisher-assistant-inline .admesh-publisher-assistant-messages:empty {
      display: none;
      flex: 0;
    }
    .admesh-publisher-assistant-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 14px 16px;
    }
    .admesh-publisher-assistant-headerMain {
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 0;
    }
    .admesh-publisher-assistant-headerText {
      min-width: 0;
    }
    .admesh-publisher-assistant-headerActions {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
    }
    .admesh-publisher-assistant-headerButton {
      width: 32px;
      height: 32px;
      border-radius: 999px;
      border: 0;
      cursor: pointer;
      font: inherit;
      font-size: 16px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.18);
      color: inherit;
    }
    .admesh-publisher-assistant-logo {
      width: 40px;
      height: 40px;
      border-radius: 999px;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      background: rgba(255, 255, 255, 0.15);
      font-size: 13px;
      font-weight: 700;
      flex-shrink: 0;
    }
    .admesh-publisher-assistant-logo img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      background: #fff;
    }
    .admesh-publisher-assistant-body {
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 14px;
      min-height: 0;
    }
    .admesh-publisher-assistant-panel:not(.admesh-publisher-assistant-inlinePanel) .admesh-publisher-assistant-body {
      height: min(620px, calc(100vh - 180px));
    }
    .admesh-publisher-assistant-panel.expanded:not(.admesh-publisher-assistant-inlinePanel) .admesh-publisher-assistant-body {
      height: min(var(--admesh-assistant-expanded-body-height), calc(100vh - 110px));
    }
    .admesh-publisher-assistant-welcome.center { text-align: center; }
    .admesh-publisher-assistant-suggestionsWrap {
      position: relative;
      display: flex;
      align-items: center;
      gap: 2px;
      min-width: 0;
      flex-shrink: 0;
      margin-top: auto;
    }
    .admesh-publisher-assistant-suggestions {
      display: flex;
      flex-wrap: nowrap;
      gap: 8px;
      flex: 1;
      min-width: 0;
      overflow-x: auto;
      overflow-y: hidden;
      padding: 0;
      scroll-snap-type: x proximity;
      overscroll-behavior-x: contain;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }
    .admesh-publisher-assistant-suggestions::-webkit-scrollbar {
      display: none;
    }
    .admesh-publisher-assistant-suggestionsNav {
      width: 24px;
      height: 24px;
      border-radius: 999px;
      border: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: rgba(17, 24, 39, 0.05);
      color: #6b7280;
      cursor: pointer;
      flex-shrink: 0;
      font: inherit;
      font-size: 12px;
      font-weight: 700;
    }
    .admesh-publisher-assistant-suggestionsNav:disabled {
      opacity: 0.35;
      cursor: default;
    }
    .admesh-publisher-assistant-suggestion {
      border: 1px solid rgba(17, 24, 39, 0.12);
      border-radius: 999px;
      padding: 7px 12px;
      cursor: pointer;
      text-align: left;
      position: relative;
      transition: transform 120ms ease;
      font: inherit;
      font-size: 12px;
      line-height: 1.35;
      flex: 0 0 auto;
      scroll-snap-align: start;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      white-space: nowrap;
    }
    .admesh-publisher-assistant-suggestionLabel {
      white-space: nowrap;
    }
    .admesh-publisher-assistant-suggestion:hover {
      transform: translateY(-1px);
    }
    .admesh-publisher-assistant-sup {
      font-size: 10px;
      font-weight: 700;
      margin-left: 4px;
      vertical-align: super;
    }
    .admesh-publisher-assistant-messages {
      display: grid;
      gap: 10px;
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      padding-right: 4px;
      align-content: start;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      -ms-overflow-style: none;
    }
    .admesh-publisher-assistant-messages::-webkit-scrollbar {
      display: none;
    }
    .admesh-publisher-assistant-message {
      max-width: 92%;
      border-radius: 16px;
      padding: 10px 12px;
      font-size: 14px;
      line-height: 1.55;
      white-space: pre-wrap;
    }
    .admesh-publisher-assistant-message.user {
      margin-left: auto;
    }
    .admesh-publisher-assistant-message.assistant {
      margin-right: auto;
      max-width: 84%;
      padding: 12px 14px;
      font-size: 13px;
      line-height: 1.7;
      color: #1f2937;
    }
    .admesh-publisher-assistant-message.loading {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      animation: admesh-publisher-assistant-pulse 1.6s ease-in-out infinite;
    }
    .admesh-publisher-assistant-loadingDot {
      width: 8px;
      height: 8px;
      border-radius: 999px;
      background: currentColor;
      opacity: 0.55;
      flex-shrink: 0;
    }
    .admesh-publisher-assistant-loadingText {
      display: inline-block;
    }
    @keyframes admesh-publisher-assistant-pulse {
      0%, 100% { opacity: 0.72; transform: translateY(0); }
      50% { opacity: 1; transform: translateY(-1px); }
    }
    .admesh-publisher-assistant-links {
      margin-top: 8px;
      display: grid;
      gap: 4px;
    }
    .admesh-publisher-assistant-links a {
      color: inherit;
      text-decoration: underline;
      font-size: 12px;
    }
    .admesh-publisher-assistant-related {
      margin-top: 14px;
      display: grid;
      gap: 10px;
    }
    .admesh-publisher-assistant-relatedHeader {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    }
    .admesh-publisher-assistant-relatedTitle {
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #6b7280;
    }
    .admesh-publisher-assistant-relatedGrid {
      display: flex;
      flex-wrap: nowrap;
      gap: 10px;
      overflow-x: auto;
      overflow-y: hidden;
      padding-bottom: 4px;
      scroll-snap-type: x proximity;
      overscroll-behavior-x: contain;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }
    .admesh-publisher-assistant-relatedGrid::-webkit-scrollbar {
      display: none;
    }
    .admesh-publisher-assistant-relatedCard {
      display: block;
      flex: 0 0 190px;
      width: 190px;
      border-radius: 14px;
      overflow: hidden;
      background: #ffffff;
      border: 1px solid rgba(17, 24, 39, 0.08);
      text-decoration: none;
      color: inherit;
      box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
      scroll-snap-align: start;
    }
    .admesh-publisher-assistant-relatedImage {
      width: 100%;
      aspect-ratio: 16 / 9;
      object-fit: cover;
      display: block;
      background: linear-gradient(135deg, #e5e7eb 0%, #f3f4f6 100%);
    }
    .admesh-publisher-assistant-relatedBody {
      padding: 10px 12px 12px;
      display: grid;
      gap: 6px;
    }
    .admesh-publisher-assistant-relatedCardTitle {
      font-size: 13px;
      font-weight: 700;
      line-height: 1.45;
      color: #111827;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .admesh-publisher-assistant-relatedPublisher {
      font-size: 12px;
      color: #6b7280;
    }
    .admesh-publisher-assistant-relatedFallback {
      width: 100%;
      aspect-ratio: 16 / 9;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%);
      color: #1d4ed8;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }
    .admesh-publisher-assistant-recommendation {
      margin-top: 12px;
      border-radius: 16px;
      border: 1px solid rgba(17, 24, 39, 0.08);
      background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
      overflow: hidden;
    }
    .admesh-publisher-assistant-recommendationHeader {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: 10px 12px;
      border-bottom: 1px solid rgba(17, 24, 39, 0.06);
    }
    .admesh-publisher-assistant-recommendationMeta {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
    }
    .admesh-publisher-assistant-recommendationLogo {
      width: 36px;
      height: 36px;
      border-radius: 999px;
      overflow: hidden;
      background: rgba(15, 23, 42, 0.06);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-size: 12px;
      font-weight: 700;
    }
    .admesh-publisher-assistant-recommendationLogo img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .admesh-publisher-assistant-recommendationLabel {
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #6366f1;
    }
    .admesh-publisher-assistant-recommendationBrand {
      font-size: 13px;
      font-weight: 600;
      color: #111827;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .admesh-publisher-assistant-recommendationBody {
      padding: 12px;
      display: grid;
      gap: 10px;
    }
    .admesh-publisher-assistant-recommendationHeadline {
      font-size: 14px;
      font-weight: 700;
      color: #111827;
      line-height: 1.4;
    }
    .admesh-publisher-assistant-recommendationDescription {
      font-size: 13px;
      color: #374151;
      line-height: 1.5;
    }
    .admesh-publisher-assistant-recommendationProducts {
      display: grid;
      gap: 6px;
    }
    .admesh-publisher-assistant-recommendationProduct {
      border-radius: 12px;
      background: rgba(99, 102, 241, 0.06);
      padding: 8px 10px;
    }
    .admesh-publisher-assistant-recommendationProductName {
      font-size: 12px;
      font-weight: 600;
      color: #111827;
    }
    .admesh-publisher-assistant-recommendationProductPrice {
      font-size: 12px;
      color: #4b5563;
      margin-top: 2px;
    }
    .admesh-publisher-assistant-recommendationCta {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 12px;
      padding: 10px 14px;
      font-size: 13px;
      font-weight: 700;
      text-decoration: none;
      background: #111827;
      color: #ffffff;
    }
    .admesh-publisher-assistant-inputRow {
      display: flex;
      gap: 8px;
      align-items: center;
      border: 1px solid rgba(17, 24, 39, 0.12);
      border-radius: 16px;
      padding: 8px;
      background: #fff;
      flex-shrink: 0;
    }
    .admesh-publisher-assistant-input {
      flex: 1;
      border: 0;
      outline: none;
      background: transparent;
      font: inherit;
      min-width: 0;
    }
    .admesh-publisher-assistant-send {
      border: 0;
      border-radius: 12px;
      padding: 10px 14px;
      font: inherit;
      font-weight: 600;
      cursor: pointer;
    }
    .admesh-publisher-assistant-launcher {
      border: 0;
      border-radius: 999px;
      padding: 12px 16px;
      font: inherit;
      font-weight: 600;
      cursor: pointer;
      box-shadow: 0 16px 40px rgba(15, 23, 42, 0.18);
      margin-top: 10px;
      width: auto;
      align-self: flex-end;
    }
    .admesh-publisher-assistant-meta {
      font-size: 11px;
      text-align: center;
      flex-shrink: 0;
    }
    .admesh-publisher-assistant-brandLink {
      color: inherit;
      text-decoration: none;
      font-weight: 600;
      opacity: 0.9;
    }
    .admesh-publisher-assistant-hidden {
      display: none !important;
    }
    @media (max-width: 640px) {
      .admesh-publisher-assistant-floating.bottom-right,
      .admesh-publisher-assistant-floating.bottom-left,
      .admesh-publisher-assistant-floating.top-right,
      .admesh-publisher-assistant-floating.top-left {
        right: 12px;
        left: 12px;
        top: auto;
        bottom: 12px;
      }
      .admesh-publisher-assistant-panel {
        width: min(100%, calc(100vw - 24px));
        max-height: min(78vh, calc(100vh - 24px));
        border-radius: 20px;
      }
      .admesh-publisher-assistant-panel.expanded,
      .admesh-publisher-assistant-inline .admesh-publisher-assistant-panel.expanded {
        left: 0;
        right: 0;
        bottom: 0;
        transform: none;
        width: 100vw;
        max-width: 100vw;
        height: min(86vh, 100dvh);
        max-height: min(86vh, 100dvh);
        border-radius: 22px 22px 0 0;
      }
      .admesh-publisher-assistant-inline .admesh-publisher-assistant-panel {
        border-radius: 24px;
        padding: 12px;
      }
      .admesh-publisher-assistant-header {
        padding: 12px 14px;
      }
      .admesh-publisher-assistant-logo {
        width: 36px;
        height: 36px;
        font-size: 12px;
      }
      .admesh-publisher-assistant-body,
      .admesh-publisher-assistant-inline .admesh-publisher-assistant-body {
        padding: 12px;
      }
      .admesh-publisher-assistant-message,
      .admesh-publisher-assistant-message.assistant {
        max-width: 100%;
        font-size: 13px;
        line-height: 1.65;
      }
      .admesh-publisher-assistant-inputRow,
      .admesh-publisher-assistant-inline .admesh-publisher-assistant-inputRow {
        padding: 10px 10px 10px 14px;
        min-height: 58px;
      }
      .admesh-publisher-assistant-input,
      .admesh-publisher-assistant-inline .admesh-publisher-assistant-input {
        font-size: 16px;
      }
      .admesh-publisher-assistant-send,
      .admesh-publisher-assistant-inline .admesh-publisher-assistant-send {
        min-width: 42px;
        min-height: 42px;
        padding: 0 14px;
      }
      .admesh-publisher-assistant-suggestionsNav {
        width: 22px;
        height: 22px;
        font-size: 11px;
      }
      .admesh-publisher-assistant-suggestion,
      .admesh-publisher-assistant-inline .admesh-publisher-assistant-suggestion {
        padding: 7px 12px;
        font-size: 12px;
      }
      .admesh-publisher-assistant-relatedCard {
        flex: 0 0 160px;
        width: 160px;
      }
      .admesh-publisher-assistant-relatedCardTitle {
        font-size: 12px;
      }
    }
  `;
  document.head.appendChild(style);
}

function normalizePath(path: string): string {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
}

function matchesPath(pathname: string, rule: string): boolean {
  const normalizedRule = normalizePath(rule.trim());
  if (normalizedRule === "/") return pathname === "/";
  if (normalizedRule.endsWith("/*")) {
    const prefix = normalizedRule.slice(0, -1);
    return pathname.startsWith(prefix);
  }
  return pathname === normalizedRule;
}

function shouldRenderOnPath(pathname: string, config: PublicAssistantConfig): boolean {
  if (!config.page_rules_enabled) return true;
  if (config.hide_on_paths.some((rule) => matchesPath(pathname, rule))) return false;
  if (!config.show_on_paths.length) return true;
  return config.show_on_paths.some((rule) => matchesPath(pathname, rule));
}

function getHostViewportWidth(): number {
  if (typeof window === "undefined") {
    return Number.POSITIVE_INFINITY;
  }

  const widths: number[] = [];
  if (typeof window.visualViewport?.width === "number" && window.visualViewport.width > 0) {
    widths.push(window.visualViewport.width);
  }
  if (document.documentElement?.clientWidth && document.documentElement.clientWidth > 0) {
    widths.push(document.documentElement.clientWidth);
  }
  if (window.innerWidth > 0) {
    widths.push(window.innerWidth);
  }

  try {
    if (window.parent && window.parent !== window && window.parent.innerWidth > 0) {
      widths.push(window.parent.innerWidth);
    }
  } catch {
    // Cross-origin parent; ignore.
  }

  return widths.length ? Math.min(...widths) : window.innerWidth;
}

function isMobileViewport(): boolean {
  return getHostViewportWidth() < 768;
}

function shouldUseMobileSlotFallback(slotType: AdSlotType): boolean {
  if (!AD_SLOT_TYPE_MOBILE_FALLBACK[slotType]) {
    return false;
  }

  const requiredWidth = AD_SLOT_TYPE_DIMENSIONS[slotType].width;
  const viewportWidth = getHostViewportWidth();

  if (viewportWidth < requiredWidth) {
    return true;
  }

  if (viewportWidth < 768) {
    return true;
  }

  if (typeof window.matchMedia === "function") {
    return window.matchMedia("(max-width: 767px)").matches;
  }

  return false;
}

function resolveEffectiveSlotType(slotType: AdSlotType | undefined): AdSlotType | undefined {
  if (!slotType) return undefined;
  if (shouldUseMobileSlotFallback(slotType)) {
    return AD_SLOT_TYPE_MOBILE_FALLBACK[slotType] ?? slotType;
  }
  return slotType;
}

function shouldRenderOnDevice(config: PublicAssistantConfig): boolean {
  const deviceTargeting = config.device_targeting || "all";
  if (deviceTargeting === "all") return true;

  const isMobile = isMobileViewport();
  if (deviceTargeting === "mobile_only") return isMobile;
  if (deviceTargeting === "desktop_only") return !isMobile;
  return true;
}

function createMessage(role: "user" | "assistant", text: string, config: PublicAssistantConfig) {
  const el = document.createElement("div");
  el.className = `admesh-publisher-assistant-message ${role}`;
  el.textContent = text;
  if (role === "user") {
    el.style.backgroundColor = config.send_button_background_color;
    el.style.color = config.send_button_text_color;
    el.style.boxShadow = "0 10px 24px rgba(15, 23, 42, 0.12)";
  } else {
    el.style.backgroundColor = "#ffffff";
    el.style.color = "#111827";
    el.style.border = "1px solid rgba(17, 24, 39, 0.08)";
  }
  return el;
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

function getCurrentScriptTag(): HTMLScriptElement | null {
  if (document.currentScript instanceof HTMLScriptElement) {
    document.currentScript.dataset.admeshProcessed = "true";
    return document.currentScript;
  }
  const scripts = Array.from(document.querySelectorAll("script"));
  const script = scripts.reverse().find((candidate) => {
    return candidate.src.includes("publisher-assistant.js") && candidate.dataset.admeshProcessed !== "true";
  }) as HTMLScriptElement | undefined;
  if (script) {
    script.dataset.admeshProcessed = "true";
    return script;
  }
  return null;
}

function getPublisherIdentifier(script: HTMLScriptElement): string | undefined {
  const agentDisplayId = script.dataset.agentDisplayId?.trim();
  if (agentDisplayId) return agentDisplayId;
  const publisherId = script.dataset.publisherId?.trim();
  if (publisherId) return publisherId;
  return undefined;
}

function parsePixelDimension(value: string | undefined): number | undefined {
  const normalized = value?.trim();
  if (!normalized) return undefined;
  const numeric = Number(normalized.replace(/px$/i, ""));
  if (!Number.isFinite(numeric) || numeric <= 0) return undefined;
  return Math.round(numeric);
}

function parseBooleanAttribute(value: string | undefined): boolean | undefined {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) return undefined;
  if (["1", "true", "yes", "on"].includes(normalized)) return true;
  if (["0", "false", "no", "off"].includes(normalized)) return false;
  return undefined;
}

function getScriptOverrides(script: HTMLScriptElement): ScriptOverrides {
  const mode =
    script.dataset.mode === "inline" || script.dataset.mode === "floating"
      ? script.dataset.mode
      : undefined;
  const size =
    script.dataset.size === "sm" ||
    script.dataset.size === "md" ||
    script.dataset.size === "lg" ||
    script.dataset.size === "xl"
      ? script.dataset.size
      : undefined;
  const position =
    script.dataset.position === "bottom-left" ||
    script.dataset.position === "top-right" ||
    script.dataset.position === "top-left" ||
    script.dataset.position === "bottom-right"
      ? script.dataset.position
      : undefined;
  const surface =
    script.dataset.surface === "ad-slot" || script.dataset.surface === "page"
      ? script.dataset.surface
      : script.dataset.adSlot === "true" || script.dataset.adServer === "true"
        ? "ad-slot"
        : undefined;

  const contextModeRaw = script.dataset.contextMode?.trim();
  const contextMode =
    contextModeRaw === "page" || contextModeRaw === "ad-slot" || contextModeRaw === "auto"
      ? contextModeRaw
      : undefined;
  const suggestionStyle =
    script.dataset.suggestionStyle === "stacked" || script.dataset.suggestionStyle === "carousel"
      ? script.dataset.suggestionStyle
      : undefined;
  const behavior =
    script.dataset.behavior === "expand" || script.dataset.behavior === "sticky"
      ? script.dataset.behavior
      : undefined;

  const slotTypeRaw = script.dataset.slotType?.trim() as AdSlotType | undefined;
  const slotType =
    slotTypeRaw && slotTypeRaw in AD_SLOT_TYPE_DIMENSIONS ? slotTypeRaw : undefined;

  // If data-slot-type is set, infer ad-slot surface automatically
  const resolvedSurface = surface ?? (slotType ? "ad-slot" : undefined);

  return {
    mode,
    position,
    size,
    surface: resolvedSurface,
    slotType,
    width: parsePixelDimension(script.dataset.width),
    height: parsePixelDimension(script.dataset.height),
    suggestionStyle,
    showHeader: parseBooleanAttribute(script.dataset.showHeader),
    showAssistantName: parseBooleanAttribute(script.dataset.showAssistantName),
    showAssistantLabel: parseBooleanAttribute(script.dataset.showAssistantLabel),
    showWelcomeMessage: parseBooleanAttribute(script.dataset.showWelcomeMessage),
    welcomeMessage: script.dataset.welcomeMessage?.trim() || undefined,
    targetSelector: script.dataset.targetSelector?.trim() || undefined,
    articleUrl: script.dataset.articleUrl?.trim() || undefined,
    contextMode,
    behavior,
  };
}

function isRunningInsideFrame() {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

function getCountryBlockCacheKey(publisherIdentifier: string): string {
  return `admesh:publisher-assistant:country-block:${encodeURIComponent(publisherIdentifier)}`;
}

function getCachedCountryBlock(publisherIdentifier: string): CountryBlockCacheEntry | null {
  try {
    const rawValue = window.localStorage.getItem(getCountryBlockCacheKey(publisherIdentifier));
    if (!rawValue) return null;
    const parsed = JSON.parse(rawValue) as Partial<CountryBlockCacheEntry>;
    if (typeof parsed.expiresAt !== "number" || parsed.expiresAt <= Date.now()) {
      window.localStorage.removeItem(getCountryBlockCacheKey(publisherIdentifier));
      return null;
    }
    return {
      expiresAt: parsed.expiresAt,
      reason: typeof parsed.reason === "string" ? parsed.reason : "country_not_allowed",
      country: typeof parsed.country === "string" ? parsed.country : null,
    };
  } catch {
    return null;
  }
}

function setCachedCountryBlock(publisherIdentifier: string, response: PublicAssistantConfigResponse) {
  if (response.reason !== "country_not_allowed") return;
  try {
    const entry: CountryBlockCacheEntry = {
      expiresAt: Date.now() + COUNTRY_BLOCK_CACHE_TTL_MS,
      reason: "country_not_allowed",
      country: response.country || null,
    };
    window.localStorage.setItem(getCountryBlockCacheKey(publisherIdentifier), JSON.stringify(entry));
  } catch {
    // Storage may be unavailable in private browsing or restricted frames.
  }
}

function getAdSlotDimensions(target?: Element | null, overrides?: ScriptOverrides) {
  const effectiveSlotType = resolveEffectiveSlotType(overrides?.slotType);
  if (effectiveSlotType) {
    const dims = AD_SLOT_TYPE_DIMENSIONS[effectiveSlotType];
    return { width: dims.width, height: dims.height };
  }
  const targetRect = target instanceof HTMLElement ? target.getBoundingClientRect() : null;
  const frameWidth = isRunningInsideFrame() ? window.innerWidth : undefined;
  const frameHeight = isRunningInsideFrame() ? window.innerHeight : undefined;
  const width = overrides?.width || frameWidth || (targetRect?.width ? Math.round(targetRect.width) : undefined);
  const height = overrides?.height || frameHeight || (targetRect?.height ? Math.round(targetRect.height) : undefined);
  return {
    width: width && width > 0 ? width : undefined,
    height: height && height > 0 ? height : undefined,
  };
}

function getAssistantDimensions(overrides?: ScriptOverrides) {
  return {
    width: overrides?.width && overrides.width > 0 ? overrides.width : undefined,
    height: overrides?.height && overrides.height > 0 ? overrides.height : undefined,
  };
}

function resolveSuggestionStyle(
  overrides: ScriptOverrides,
  config: PublicAssistantConfig,
): SuggestionStyle {
  if (overrides.suggestionStyle) {
    return overrides.suggestionStyle;
  }
  const effectiveSlotType = resolveEffectiveSlotType(overrides.slotType);
  if (effectiveSlotType) {
    return AD_SLOT_TYPE_SUGGESTION_STYLE[effectiveSlotType] ?? "carousel";
  }
  return config.suggestion_style || "carousel";
}

function resolveAdSlotBehavior(
  slotType: AdSlotType | undefined,
  behavior: AdSlotBehavior | undefined,
): AdSlotBehavior {
  if (slotType && AD_SLOT_TYPE_BEHAVIOR[slotType]) {
    return AD_SLOT_TYPE_BEHAVIOR[slotType]!;
  }
  return behavior || "expand";
}

function getDefaultSuggestions(mode: DeploymentMode, adsEnabled: boolean): AssistantSuggestion[] {
  if (mode === "inline") {
    return [
      { text: "How do product feeds improve Demand Gen campaign performance?", type: "information" },
      { text: "What are the requirements to link Merchant Center to Google Ads?", type: "information" },
      { text: "Why is my product feed not appearing in my campaign?", type: adsEnabled ? "action" : "information" },
    ];
  }

  return [
    { text: "What did this publisher write about AI search this week?", type: "information" },
    { text: "Top AI search stories from this week", type: "information" },
    { text: "Publisher coverage on search monetization trends", type: adsEnabled ? "action" : "information" },
  ];
}

function createSessionId(url: string) {
  try {
    const parsed = new URL(url);
    const segments = parsed.pathname.split("/").filter(Boolean);
    const slug = segments[segments.length - 1] || "publisher-assistant";
    return `session_${Date.now()}_${slug.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
  } catch {
    return `session_${Date.now()}_publisher-assistant`;
  }
}

function isResolvedPageUrl(value?: string) {
  const trimmed = value?.trim();
  if (!trimmed || /%%[^%]+%%/.test(trimmed)) {
    return false;
  }
  try {
    const parsed = new URL(trimmed);
    return Boolean(parsed.protocol && parsed.hostname);
  } catch {
    return false;
  }
}

function getUsableWindowHref() {
  try {
    const href = window.location.href?.trim();
    if (isResolvedPageUrl(href)) {
      return href;
    }
  } catch {
    return undefined;
  }
  return undefined;
}

function resolveAnalyticsPageUrl(surface: AssistantSurface, articleUrl?: string) {
  const currentHref = getUsableWindowHref();
  if (currentHref) {
    return currentHref;
  }
  const trimmedArticleUrl = articleUrl?.trim();
  if (isResolvedPageUrl(trimmedArticleUrl)) {
    return trimmedArticleUrl;
  }
  if (surface === "ad-slot") {
    return undefined;
  }
  return window.location.href;
}

function buildTheme(config: PublicAssistantConfig) {
  return {
    mode: "light" as const,
    primaryColor: config.send_button_background_color,
    secondaryColor: config.meta_text_color,
    backgroundColor: config.input_background_color,
    surfaceColor: "#ffffff",
    borderColor: "rgba(17, 24, 39, 0.08)",
    textColor: config.input_text_color,
    textSecondaryColor: config.meta_text_color,
    primaryTextColor: config.send_button_text_color,
    borderRadius: "24px",
    spacing: {
      small: "0.5rem",
      medium: "1rem",
      large: "1.5rem",
    },
    shadows: {
      small: "0 1px 2px rgba(15, 23, 42, 0.06)",
      medium: "0 8px 22px rgba(15, 23, 42, 0.08)",
      large: "0 24px 60px rgba(15, 23, 42, 0.16)",
    },
  };
}

function createAssistantApp({
  config,
  runtimeApiKey,
  sessionId,
  mode,
  floatingPosition,
  size,
  surface,
  panelWidth,
  panelHeight,
  suggestionStyle,
  showHeader,
  showAssistantName,
  showAssistantLabel,
  showWelcomeMessage,
  welcomeMessage,
  articleUrl,
  contextMode,
  agentDisplayId,
  adSlotBehavior,
  showCompactSlotHeader,
  showCompactWelcomeMessage,
  showCompactSlotInput,
  showCompactSlotDisclaimer,
}: {
  config: PublicAssistantConfig;
  runtimeApiKey: string;
  sessionId: string;
  mode: DeploymentMode;
  floatingPosition: FloatingPosition;
  size: AssistantSize;
  surface: AssistantSurface;
  panelWidth?: number;
  panelHeight?: number;
  suggestionStyle?: SuggestionStyle;
  showHeader?: boolean;
  showAssistantName?: boolean;
  showAssistantLabel?: boolean;
  showWelcomeMessage?: boolean;
  welcomeMessage?: string;
  articleUrl?: string;
  contextMode?: PageContextMode;
  agentDisplayId?: string;
  adSlotBehavior?: AdSlotBehavior;
  showCompactSlotHeader?: boolean;
  showCompactWelcomeMessage?: boolean;
  showCompactSlotInput?: boolean;
  showCompactSlotDisclaimer?: boolean;
}) {
  const resolvedContextMode: PageContextMode =
    contextMode || (surface === "ad-slot" ? "ad-slot" : "auto");

  return React.createElement(
    AdMeshProvider,
    {
      apiKey: runtimeApiKey,
      sessionId,
      apiBaseUrl: DEFAULT_API_BASE_URL,
      theme: buildTheme(config),
      articleUrl,
      contextMode: resolvedContextMode,
      agentDisplayId,
    },
    React.createElement(AdMeshIntentAssistant, {
      variant: "default",
      layout: mode,
      surface,
      adSlotBehavior,
      showCompactSlotHeader,
      showCompactWelcomeMessage,
      showCompactSlotInput,
      showCompactSlotDisclaimer,
      position: floatingPosition,
      size,
      panelWidth,
      panelHeight,
      analyticsPageUrl: resolveAnalyticsPageUrl(surface, articleUrl),
      suggestionStyle: suggestionStyle ?? "carousel",
      headerTitle: config.assistant_name,
      headerLogoUrl: config.logo_url,
      showHeader: showHeader ?? true,
      showHeaderTitle: showAssistantName ?? (config.show_assistant_name !== false),
      showSubtitle: showAssistantLabel ?? (config.show_assistant_label !== false),
      triggerLabel: config.assistant_name,
      placeholder: config.input_placeholder,
      disclaimerText: config.disclaimer_text,
      poweredByLabel: "Powered by AdMesh",
      subtitle: (showAssistantLabel ?? (config.show_assistant_label !== false)) ? "Publisher Assistant" : "",
      adsEnabled: config.ads_enabled,
      showWelcomeMessage: showWelcomeMessage === true,
      welcomeMessage: welcomeMessage || config.welcome_description,
    })
  );
}

type MountedAssistantState = {
  script: HTMLScriptElement;
  mountNode: HTMLDivElement;
  reactRoot: ReactDOM.Root;
  overrides: ScriptOverrides;
  config: PublicAssistantConfig;
  runtimeApiKey: string;
  sessionId: string;
  mode: DeploymentMode;
  floatingPosition: FloatingPosition;
  surface: AssistantSurface;
  contextMode: PageContextMode;
  articleUrl?: string;
  agentDisplayId: string;
  size: AssistantSize;
  targetSelector?: string;
};

function applyAdSlotDimensions(
  mountNode: HTMLDivElement,
  target: HTMLElement | null,
  dimensions: { width?: number; height?: number },
) {
  if (dimensions.width) {
    mountNode.style.width = `${dimensions.width}px`;
    mountNode.style.maxWidth = isMobileViewport() ? "100%" : "";
  }
  if (dimensions.height) {
    mountNode.style.height = `${dimensions.height}px`;
  }
  if (target) {
    if (dimensions.width) {
      target.style.width = `${dimensions.width}px`;
      target.style.maxWidth = isMobileViewport() ? "100%" : "";
    }
    if (dimensions.height) {
      target.style.height = `${dimensions.height}px`;
    }
    target.style.boxSizing = "border-box";
  }
}

function renderMountedAssistant(state: MountedAssistantState) {
  const rawSlotType = state.overrides.slotType;
  const effectiveSlotType = resolveEffectiveSlotType(rawSlotType);
  const target =
    state.mode === "inline" && state.targetSelector
      ? document.querySelector(state.targetSelector)
      : null;

  let assistantDimensions = getAssistantDimensions(state.overrides);
  if (state.surface === "ad-slot") {
    assistantDimensions = getAdSlotDimensions(target, state.overrides);
  }

  state.mountNode.dataset.admeshRawSlotType = rawSlotType ?? "";
  state.mountNode.dataset.admeshEffectiveSlotType = effectiveSlotType ?? "";
  applyAdSlotDimensions(
    state.mountNode,
    target instanceof HTMLElement ? target : null,
    assistantDimensions,
  );

  state.reactRoot.render(
    createAssistantApp({
      config: state.config,
      runtimeApiKey: state.runtimeApiKey,
      sessionId: state.sessionId,
      mode: state.mode,
      floatingPosition: state.floatingPosition,
      size: state.size,
      surface: state.surface,
      panelWidth: assistantDimensions.width,
      panelHeight: assistantDimensions.height,
      suggestionStyle: resolveSuggestionStyle(state.overrides, state.config),
      showHeader: state.overrides.showHeader,
      showAssistantName: state.overrides.showAssistantName,
      showAssistantLabel: state.overrides.showAssistantLabel,
      showWelcomeMessage: state.overrides.showWelcomeMessage,
      welcomeMessage: state.overrides.welcomeMessage,
      articleUrl: state.articleUrl,
      contextMode: state.contextMode,
      agentDisplayId: state.agentDisplayId,
      adSlotBehavior:
        state.surface === "ad-slot"
          ? resolveAdSlotBehavior(rawSlotType, state.overrides.behavior)
          : undefined,
      showCompactSlotHeader: effectiveSlotType
        ? (AD_SLOT_TYPE_SHOW_HEADER[effectiveSlotType] ?? true)
        : true,
      showCompactWelcomeMessage: effectiveSlotType
        ? (AD_SLOT_TYPE_SHOW_WELCOME_MESSAGE[effectiveSlotType] ?? true)
        : true,
      showCompactSlotInput: effectiveSlotType
        ? (AD_SLOT_TYPE_SHOW_INPUT[effectiveSlotType] ?? true)
        : true,
      showCompactSlotDisclaimer: effectiveSlotType
        ? (AD_SLOT_TYPE_SHOW_DISCLAIMER[effectiveSlotType] ?? true)
        : true,
    }),
  );
}

function attachAdSlotViewportWatcher(state: MountedAssistantState) {
  const rawSlotType = state.overrides.slotType;
  if (!rawSlotType || !AD_SLOT_TYPE_MOBILE_FALLBACK[rawSlotType]) {
    return;
  }

  let lastEffective = resolveEffectiveSlotType(rawSlotType);
  const handleViewportChange = () => {
    const nextEffective = resolveEffectiveSlotType(rawSlotType);
    if (nextEffective === lastEffective) {
      return;
    }
    lastEffective = nextEffective;
    renderMountedAssistant(state);
  };

  window.addEventListener("resize", handleViewportChange);
  window.visualViewport?.addEventListener("resize", handleViewportChange);
  window.visualViewport?.addEventListener("scroll", handleViewportChange);
}

function getExpandedSizeConfig(size: AssistantSize = "md") {
  return EXPANDED_SIZE_CONFIG[size] || EXPANDED_SIZE_CONFIG.md;
}

function buildAssistantRoot(
  config: PublicAssistantConfig,
  apiBaseUrl: string,
  publisherId: string,
  mode: DeploymentMode,
  size: AssistantSize = "md"
) {
  const root = document.createElement("div");
  root.className = "admesh-publisher-assistant-root";
  const expandedSize = getExpandedSizeConfig(size);
  root.style.setProperty("--admesh-assistant-expanded-width", expandedSize.width);
  root.style.setProperty("--admesh-assistant-expanded-height", expandedSize.height);
  root.style.setProperty("--admesh-assistant-expanded-body-height", expandedSize.bodyHeight);

  const panel = document.createElement("div");
  panel.className = "admesh-publisher-assistant-panel";
  if (mode === "inline") {
    panel.classList.add("admesh-publisher-assistant-inlinePanel");
  }

  const inlineBackdrop = document.createElement("div");
  inlineBackdrop.className = "admesh-publisher-assistant-inlineSheetBackdrop admesh-publisher-assistant-hidden";
  const floatingBackdrop = document.createElement("div");
  floatingBackdrop.className = "admesh-publisher-assistant-sheetBackdrop admesh-publisher-assistant-hidden";

  const header = document.createElement("div");
  header.className = "admesh-publisher-assistant-header";
  header.style.backgroundColor = config.header_background_color;
  header.style.color = config.header_text_color;

  const headerMain = document.createElement("div");
  headerMain.className = "admesh-publisher-assistant-headerMain";

  const logo = document.createElement("div");
  logo.className = "admesh-publisher-assistant-logo";
  if (config.logo_url) {
    const img = document.createElement("img");
    img.src = config.logo_url;
    img.alt = `${config.assistant_name} logo`;
    logo.appendChild(img);
  } else {
    logo.textContent = (config.assistant_name || "AA").slice(0, 2).toUpperCase();
  }

  const headerText = document.createElement("div");
  headerText.className = "admesh-publisher-assistant-headerText";
  const title = document.createElement("div");
  title.textContent = config.assistant_name;
  title.style.fontWeight = "600";
  title.style.color = config.header_text_color;
  const subtitle = document.createElement("div");
  subtitle.textContent = "Publisher Assistant";
  subtitle.style.fontSize = "12px";
  subtitle.style.opacity = "0.8";
  subtitle.style.color = config.header_text_color;
  if (config.show_assistant_name !== false) {
    headerText.appendChild(title);
  }
  if (config.show_assistant_label !== false) {
    headerText.appendChild(subtitle);
  }
  headerMain.append(logo);
  if (headerText.childNodes.length > 0) {
    headerMain.appendChild(headerText);
  }
  header.append(headerMain);

  let minimizeButton: HTMLButtonElement | null = null;
  if (mode === "floating" || mode === "inline") {
    const headerActions = document.createElement("div");
    headerActions.className = "admesh-publisher-assistant-headerActions";

    const expandButton = document.createElement("button");
    expandButton.type = "button";
    expandButton.className = "admesh-publisher-assistant-headerButton";
    expandButton.textContent = "⤢";
    expandButton.setAttribute("aria-label", "Expand assistant");

    minimizeButton = document.createElement("button");
    minimizeButton.type = "button";
    minimizeButton.className = "admesh-publisher-assistant-headerButton";
    minimizeButton.textContent = mode === "inline" ? "×" : "−";
    minimizeButton.setAttribute("aria-label", mode === "inline" ? "Close assistant" : "Minimize assistant");

    let expanded = false;
    const setExpandedState = (nextExpanded: boolean) => {
      expanded = nextExpanded;
      panel.classList.toggle("expanded", expanded);
      if (mode === "inline") {
        inlineBackdrop.classList.toggle("admesh-publisher-assistant-hidden", !expanded);
      } else if (mode === "floating") {
        floatingBackdrop.classList.toggle("admesh-publisher-assistant-hidden", !expanded);
      } else {
        expandButton.textContent = expanded ? "⤡" : "⤢";
        expandButton.setAttribute("aria-label", expanded ? "Restore assistant size" : "Expand assistant");
      }
      if (mode === "floating") {
        expandButton.textContent = expanded ? "⤡" : "⤢";
        expandButton.setAttribute("aria-label", expanded ? "Restore assistant size" : "Expand assistant");
      }
      syncSurfaceState(expanded);
    };
    expandButton.addEventListener("click", () => {
      setExpandedState(!expanded);
    });
    (panel as HTMLDivElement & { __setExpandedState?: (expanded: boolean) => void }).__setExpandedState = setExpandedState;

    if (mode === "inline") {
      expandButton.style.display = "none";
    }

    headerActions.append(expandButton, minimizeButton);
    header.append(headerActions);
  }

  const body = document.createElement("div");
  body.className = "admesh-publisher-assistant-body";

  const welcome = document.createElement("div");
  welcome.className = `admesh-publisher-assistant-welcome ${config.welcome_alignment}`;
  const welcomeTitle = document.createElement("div");
  welcomeTitle.textContent = config.welcome_title;
  welcomeTitle.style.color = config.welcome_title_color;
  welcomeTitle.style.fontWeight = "600";
  welcomeTitle.style.fontSize = "16px";
  const welcomeDescription = document.createElement("div");
  welcomeDescription.textContent = config.welcome_description;
  welcomeDescription.style.color = config.welcome_description_color;
  welcomeDescription.style.fontSize = "14px";
  welcomeDescription.style.marginTop = "4px";
  welcome.append(welcomeTitle, welcomeDescription);

  const suggestionsWrap = document.createElement("div");
  suggestionsWrap.className = "admesh-publisher-assistant-suggestionsWrap";

  const suggestionsPrev = document.createElement("button");
  suggestionsPrev.type = "button";
  suggestionsPrev.className = "admesh-publisher-assistant-suggestionsNav";
  suggestionsPrev.textContent = "‹";
  suggestionsPrev.setAttribute("aria-label", "Scroll suggestions left");

  const suggestions = document.createElement("div");
  suggestions.className = "admesh-publisher-assistant-suggestions";

  const suggestionsNext = document.createElement("button");
  suggestionsNext.type = "button";
  suggestionsNext.className = "admesh-publisher-assistant-suggestionsNav";
  suggestionsNext.textContent = "›";
  suggestionsNext.setAttribute("aria-label", "Scroll suggestions right");

  suggestionsWrap.append(suggestionsPrev, suggestions, suggestionsNext);
  const defaultSuggestions = getDefaultSuggestions(mode, config.ads_enabled);
  const pageSessionId = createSessionId(window.location.href);
  const exposedSponsoredRecommendations = new Set<string>();
  let suggestionExposureCleanups: Array<() => void> = [];

  const messages = document.createElement("div");
  messages.className = "admesh-publisher-assistant-messages";

  const inputRow = document.createElement("div");
  inputRow.className = "admesh-publisher-assistant-inputRow";

  const input = document.createElement("input");
  input.className = "admesh-publisher-assistant-input";
  input.placeholder = config.input_placeholder;
  input.style.backgroundColor = config.input_background_color;
  input.style.color = config.input_text_color;

  const send = document.createElement("button");
  send.className = "admesh-publisher-assistant-send";
  send.textContent = config.send_button_label;
  send.style.backgroundColor = config.send_button_background_color;
  send.style.color = config.send_button_text_color;

  const poweredBy = document.createElement("div");
  poweredBy.className = "admesh-publisher-assistant-meta";
  poweredBy.style.color = config.meta_text_color;
  poweredBy.append("Powered by ");
  const brandLink = document.createElement("a");
  brandLink.className = "admesh-publisher-assistant-brandLink";
  brandLink.href = "https://useadmesh.com";
  brandLink.target = "_blank";
  brandLink.rel = "noopener noreferrer";
  brandLink.textContent = "AdMesh";
  poweredBy.appendChild(brandLink);

  const disclaimer = document.createElement("div");
  disclaimer.className = "admesh-publisher-assistant-meta";
  disclaimer.textContent = config.disclaimer_text;
  disclaimer.style.color = config.meta_text_color;

  let hasConversation = false;

  function syncSurfaceState(expanded: boolean) {
    if (!hasConversation) {
      messages.classList.add("admesh-publisher-assistant-hidden");
      suggestions.classList.remove("admesh-publisher-assistant-hidden");
      welcome.classList.toggle("admesh-publisher-assistant-hidden", mode === "inline");
      return;
    }

    if (expanded) {
      messages.classList.remove("admesh-publisher-assistant-hidden");
      suggestions.classList.add("admesh-publisher-assistant-hidden");
      welcome.classList.add("admesh-publisher-assistant-hidden");
      return;
    }

    messages.classList.add("admesh-publisher-assistant-hidden");
    suggestions.classList.remove("admesh-publisher-assistant-hidden");
    welcome.classList.toggle("admesh-publisher-assistant-hidden", mode === "inline");
  }

  function updateSuggestionNav() {
    const maxScrollLeft = Math.max(0, suggestions.scrollWidth - suggestions.clientWidth);
    suggestionsPrev.disabled = suggestions.scrollLeft <= 4;
    suggestionsNext.disabled = suggestions.scrollLeft >= maxScrollLeft - 4;
  }

  function scrollSuggestions(direction: "left" | "right") {
    const delta = Math.max(160, Math.floor(suggestions.clientWidth * 0.75));
    suggestions.scrollBy({
      left: direction === "left" ? -delta : delta,
      behavior: "smooth",
    });
  }

  function trackFollowupExposureWhenViewable(suggestion: AssistantSuggestion, element: HTMLElement) {
    const recommendationId = suggestion.recommendation?.recommendation_id;
    const exposureUrl = suggestion.recommendation?.tracking?.followup_exposure_url;
    if (!recommendationId || !exposureUrl || exposedSponsoredRecommendations.has(recommendationId)) {
      return;
    }
    if (typeof IntersectionObserver === "undefined") {
      return;
    }

    let viewableTimer: ReturnType<typeof setTimeout> | null = null;
    let observer: IntersectionObserver | null = null;

    const cleanup = () => {
      if (viewableTimer) {
        clearTimeout(viewableTimer);
        viewableTimer = null;
      }
      observer?.disconnect();
      observer = null;
    };
    suggestionExposureCleanups.push(cleanup);

    const fire = () => {
      if (exposedSponsoredRecommendations.has(recommendationId)) {
        cleanup();
        return;
      }
      exposedSponsoredRecommendations.add(recommendationId);
      cleanup();
      void fetch(exposureUrl, { method: "GET", keepalive: true }).catch(() => {
        exposedSponsoredRecommendations.delete(recommendationId);
      });
    };

    observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) {
          return;
        }

        if (entry.intersectionRatio >= 0.5) {
          if (!viewableTimer) {
            viewableTimer = setTimeout(fire, 1000);
          }
          return;
        }

        if (viewableTimer) {
          clearTimeout(viewableTimer);
          viewableTimer = null;
        }
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1], rootMargin: "0px" }
    );

    observer.observe(element);
  }

  function fireFollowupEngagement(suggestion: AssistantSuggestion) {
    const recommendationId = suggestion.recommendation?.recommendation_id;
    const engagementUrl = suggestion.recommendation?.tracking?.followup_engagement_url;
    if (!recommendationId || !engagementUrl) {
      return;
    }
    void fetch(engagementUrl, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: pageSessionId }),
    }).catch(() => undefined);
  }

  function createRecommendationCard(
    input: AssistantSuggestion | AssistantSuggestion["recommendation"] | undefined
  ) {
    const recommendationEnvelope =
      input && "recommendation_id" in input
        ? input
        : input && "recommendation" in input
          ? input.recommendation
          : undefined;
    if (!recommendationEnvelope) return null;

    const card = document.createElement("div");
    card.className = "admesh-publisher-assistant-recommendationSdkMount";

    const root = ReactDOM.createRoot(card);
    root.render(
      React.createElement(AdMeshCompactCard, {
        recommendations: [recommendationEnvelope as PlatformResponse],
        sessionId: pageSessionId,
        triggerExposureUrl: true,
      })
    );

    return card;
  }

  function createRelatedSection(
    items: Array<{
      title?: string;
      url: string;
      snippet?: string;
      image_url?: string | null;
      publisher?: string | null;
    }>
  ) {
    if (!items.length) return null;

    const section = document.createElement("div");
    section.className = "admesh-publisher-assistant-related";

    const headerRow = document.createElement("div");
    headerRow.className = "admesh-publisher-assistant-relatedHeader";
    const title = document.createElement("div");
    title.className = "admesh-publisher-assistant-relatedTitle";
    title.textContent = "Related";
    headerRow.appendChild(title);

    const grid = document.createElement("div");
    grid.className = "admesh-publisher-assistant-relatedGrid";

    for (const item of items.slice(0, 4)) {
      if (!item.url) continue;

      const card = document.createElement("a");
      card.className = "admesh-publisher-assistant-relatedCard";
      card.href = item.url;
      card.target = "_blank";
      card.rel = "noopener noreferrer";

      if (item.image_url) {
        const image = document.createElement("img");
        image.className = "admesh-publisher-assistant-relatedImage";
        image.src = item.image_url;
        image.alt = item.title || "Related article";
        card.appendChild(image);
      } else {
        const fallback = document.createElement("div");
        fallback.className = "admesh-publisher-assistant-relatedFallback";
        fallback.textContent = "Related";
        card.appendChild(fallback);
      }

      const bodyCard = document.createElement("div");
      bodyCard.className = "admesh-publisher-assistant-relatedBody";

      const cardTitle = document.createElement("div");
      cardTitle.className = "admesh-publisher-assistant-relatedCardTitle";
      cardTitle.textContent = item.title || item.url;
      bodyCard.appendChild(cardTitle);

      if (item.publisher) {
        const publisher = document.createElement("div");
        publisher.className = "admesh-publisher-assistant-relatedPublisher";
        publisher.textContent = item.publisher;
        bodyCard.appendChild(publisher);
      }

      card.appendChild(bodyCard);
      grid.appendChild(card);
    }

    if (!grid.childElementCount) return null;
    section.append(headerRow, grid);
    return section;
  }

  async function submitQuery(query: string, sponsoredSuggestion?: AssistantSuggestion) {
    const trimmed = query.trim();
    if (!trimmed) return;

    const loadingSteps = [
      "Searching trusted sources...",
      "Reviewing publisher coverage...",
      "Checking relevant context...",
      "Comparing supporting signals...",
      "Preparing the clearest answer...",
      "Finishing the response...",
    ];

    hasConversation = true;
    syncSurfaceState(true);
    messages.appendChild(createMessage("user", trimmed, config));
    messages.scrollTop = messages.scrollHeight;
    input.value = "";

    const thinking = createMessage("assistant", loadingSteps[0], config);
    thinking.classList.add("loading");
    thinking.textContent = "";
    const loadingDot = document.createElement("span");
    loadingDot.className = "admesh-publisher-assistant-loadingDot";
    const loadingText = document.createElement("span");
    loadingText.className = "admesh-publisher-assistant-loadingText";
    loadingText.textContent = loadingSteps[0];
    thinking.append(loadingDot, loadingText);
    messages.appendChild(thinking);
    messages.scrollTop = messages.scrollHeight;

    let loadingStepIndex = 0;
    const loadingInterval = window.setInterval(() => {
      loadingStepIndex = (loadingStepIndex + 1) % loadingSteps.length;
      loadingText.textContent = loadingSteps[loadingStepIndex];
    }, 3000);

    window.clearInterval(loadingInterval);
    messages.replaceChild(
      createMessage("assistant", "Legacy inline runtime is disabled. This embed now uses the AdMeshIntentAssistant React runtime.", config),
      thinking
    );
    messages.scrollTop = messages.scrollHeight;
  }

  function renderSuggestions(suggestionValues: AssistantSuggestion[]) {
    suggestionExposureCleanups.forEach((cleanup) => cleanup());
    suggestionExposureCleanups = [];
    suggestions.innerHTML = "";
    for (const suggestion of suggestionValues) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "admesh-publisher-assistant-suggestion";
      button.style.backgroundColor = config.suggestion_background_color;
      button.style.color = config.suggestion_text_color;
      const label = document.createElement("span");
      label.className = "admesh-publisher-assistant-suggestionLabel";
      label.textContent = suggestion.text;
      button.appendChild(label);
      if (config.ads_enabled && suggestion.is_sponsored) {
        const sup = document.createElement("sup");
        sup.className = "admesh-publisher-assistant-sup";
        sup.textContent = "Ad";
        button.appendChild(sup);
      }
      if (suggestion.is_sponsored) {
        trackFollowupExposureWhenViewable(suggestion, button);
      }
      button.addEventListener("click", () => {
        if (mode === "floating" || mode === "inline") {
          panel.classList.remove("admesh-publisher-assistant-hidden");
          (panel as HTMLDivElement & { __setExpandedState?: (expanded: boolean) => void }).__setExpandedState?.(true);
        }
        if (suggestion.is_sponsored) {
          fireFollowupEngagement(suggestion);
        }
        void submitQuery(suggestion.text, suggestion);
      });
      suggestions.appendChild(button);
    }
    suggestions.scrollLeft = 0;
    updateSuggestionNav();
  }

  suggestionsPrev.addEventListener("click", () => {
    scrollSuggestions("left");
  });
  suggestionsNext.addEventListener("click", () => {
    scrollSuggestions("right");
  });
  suggestions.addEventListener("scroll", updateSuggestionNav);
  window.addEventListener("resize", updateSuggestionNav);
  syncSurfaceState(false);

  renderSuggestions(defaultSuggestions);

  send.addEventListener("click", () => {
    if (mode === "floating" || mode === "inline") {
      panel.classList.remove("admesh-publisher-assistant-hidden");
      (panel as HTMLDivElement & { __setExpandedState?: (expanded: boolean) => void }).__setExpandedState?.(true);
    }
    void submitQuery(input.value);
  });
  input.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (mode === "floating" || mode === "inline") {
        panel.classList.remove("admesh-publisher-assistant-hidden");
        (panel as HTMLDivElement & { __setExpandedState?: (expanded: boolean) => void }).__setExpandedState?.(true);
      }
      void submitQuery(input.value);
    }
  });

  inputRow.append(input, send);
  body.append(welcome, suggestionsWrap, messages, inputRow, poweredBy, disclaimer);
  panel.append(header, body);
  root.append(inlineBackdrop, floatingBackdrop, panel);

  return { root, panel, minimizeButton };
}

async function mountPublisherAssistant() {
  const script = getCurrentScriptTag();
  if (!script) {
    console.error("[AdMesh] Publisher assistant embed script could not find its script tag.");
    return;
  }

  const publisherIdentifier = getPublisherIdentifier(script);
  if (!publisherIdentifier) {
    console.error("[AdMesh] Missing required data-agent-display-id or data-publisher-id attribute.");
    return;
  }

  const overrides = getScriptOverrides(script);

  try {
    const cachedCountryBlock = getCachedCountryBlock(publisherIdentifier);
    if (cachedCountryBlock) {
      console.warn(
        `[AdMesh] Publisher assistant is not ready for publisher ${publisherIdentifier}: ${cachedCountryBlock.reason}`
      );
      return;
    }

    const response = await fetchJson<PublicAssistantConfigResponse>(
      `${DEFAULT_API_BASE_URL}/publisher-assistant/public/${encodeURIComponent(publisherIdentifier)}/config`
    );
    const config = response.config;
    if (response.ready === false) {
      if (response.reason === "missing_inline_target_selector" && overrides.targetSelector) {
        console.warn(
          `[AdMesh] Publisher assistant config is missing an inline target selector; using script override ${overrides.targetSelector}.`
        );
      } else {
        console.warn(
          `[AdMesh] Publisher assistant is not ready for publisher ${publisherIdentifier}: ${response.reason || "unknown_reason"}`
        );
        setCachedCountryBlock(publisherIdentifier, response);
        return;
      }
    }
    const mode = overrides.mode || config.deployment_mode || "inline";
    const floatingPosition = overrides.position || config.floating_position;
    const surface = overrides.surface || (mode === "inline" && isRunningInsideFrame() ? "ad-slot" : "page");
    const contextMode: PageContextMode =
      overrides.contextMode || (surface === "ad-slot" ? "ad-slot" : "auto");
    const articleUrl = overrides.articleUrl;
    const targetSelector = overrides.targetSelector || config.inline_target_selector?.trim();
    const runtimeApiKey = response.runtime_api_key?.trim() || "";

    if (!shouldRenderOnPath(window.location.pathname, config)) {
      return;
    }

    if (!shouldRenderOnDevice(config)) {
      return;
    }

    if (!runtimeApiKey) {
      console.warn(`[AdMesh] No active publisher runtime API key found for ${publisherIdentifier}.`);
      return;
    }

    const inlineTarget =
      mode === "inline" && targetSelector
        ? document.querySelector(targetSelector)
        : null;
    if (mode === "inline" && targetSelector && !inlineTarget) {
      console.error(`[AdMesh] Inline target not found for selector: ${targetSelector}`);
      return;
    }

    ensureStyles();

    const sessionId = createSessionId(window.location.href);
    const size = overrides.size || "md";
    const mountNode = document.createElement("div");
    mountNode.className = "admesh-publisher-assistant-reactMount";
    mountNode.dataset.admeshSurface = surface;

    const reactRoot = ReactDOM.createRoot(mountNode);
    const mountState: MountedAssistantState = {
      script,
      mountNode,
      reactRoot,
      overrides,
      config,
      runtimeApiKey,
      sessionId,
      mode,
      floatingPosition,
      surface,
      contextMode,
      articleUrl,
      agentDisplayId: publisherIdentifier,
      size,
      targetSelector,
    };

    renderMountedAssistant(mountState);
    attachAdSlotViewportWatcher(mountState);

    if (mode === "inline") {
      if (targetSelector) {
        inlineTarget?.appendChild(mountNode);
        return;
      }
      script.insertAdjacentElement("afterend", mountNode);
      return;
    }

    document.body.appendChild(mountNode);
  } catch (error) {
    console.error("[AdMesh] Failed to initialize publisher assistant.", error);
  }
}

void mountPublisherAssistant();
